import React, { useState, useRef, useEffect, useMemo } from 'react'
import moment from 'moment-timezone';

import { Cancel, CheckCircle, Error, ReportProblem, Launch, SchoolOutlined, CreditCard, DescriptionOutlined } from "@mui/icons-material";
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import UserAvatar from "../components/UserAvatar";
import { ReactComponent as CredentialIcon } from "../icons/credentialIcon.svg";

import {  CurrentRequirementsSchema, MembershipSchema, MembershipStatusType, MemberRecordDropdownOptionsType } from "../data/types";
import { useLoadMember, useGetMemberRequirementsStatus, useUpdateMembership } from '../hooks/memberhooks';
import { useGetMemberAgreement } from '../hooks/communityhook';
// import { useLoadRequirements } from "../hooks/kitchenhooks";


import { styleThousandCommas, getMemberStatusColor, getMemberStatusUpdateOptions, getCredentialExpirationInfo, getNextBillingCycleLabel } from "../utils/helpers";
import CredentialPreview from "./CredentialPreview";
import { NewMessageModal } from './AppModal';
import { toast } from "sonner";
import { Skeleton } from "../pages/shared/Skeleton";

export const MEMBERSHIP_AGREEMENT_REQUIREMENT_ID = 'membership-agreement';
export const MEMBERSHIP_AGREEMENT_REQUIREMENT_CODE = 'MEMBER_AGREEMENT';
export const ORIENTATION_TRAINING_CATEGORY = 'orientation_training';

export const isMembershipAgreementRequirement = (
    requirement: Pick<CurrentRequirementsSchema, 'id' | 'code'>
): boolean =>
    requirement.id === MEMBERSHIP_AGREEMENT_REQUIREMENT_ID ||
    requirement.code === MEMBERSHIP_AGREEMENT_REQUIREMENT_CODE;

export const isOrientationTrainingRequirement = (
    requirement: Pick<CurrentRequirementsSchema, 'category'>
): boolean => requirement.category === ORIENTATION_TRAINING_CATEGORY;

export const isPaymentRequirement = (
    requirement: Pick<CurrentRequirementsSchema, 'code'>
): boolean => requirement.code === 'PAY';

const getRequirementStatusLabel = (
    requirement: CurrentRequirementsSchema,
    isMembershipAgreement: boolean,
    isOrientationTraining: boolean,
    isPayment: boolean
): { label: string; tone: 'success' | 'warning' | 'error' } => {
    if (requirement.is_met) {
        if (isMembershipAgreement) return { label: 'Signed', tone: 'success' };
        if (isOrientationTraining) return { label: 'Completed', tone: 'success' };
        if (isPayment) return { label: 'On File', tone: 'success' };
        return { label: 'Approved', tone: 'success' };
    }
    if (requirement.is_required && requirement.presigned_url && !isMembershipAgreement && !isPayment) {
        if (isOrientationTraining) return { label: 'Pending Review', tone: 'warning' };
        return { label: 'Pending Approval', tone: 'warning' };
    }
    if (isMembershipAgreement) return { label: 'Not Signed', tone: 'error' };
    if (isOrientationTraining) return { label: 'Not Completed', tone: 'error' };
    if (isPayment) return { label: 'Not On File', tone: 'error' };
    return { label: 'Not Submitted', tone: 'error' };
};

const isRequirementRowClickable = (
    requirement: CurrentRequirementsSchema,
    isMembershipAgreement: boolean,
    memberAgreementFileUrl?: string
): boolean => {
    if (isPaymentRequirement(requirement)) return false;
    if (isMembershipAgreement) return !!memberAgreementFileUrl;
    return true;
};

export const getRequirementRowKey = (
    requirement: CurrentRequirementsSchema,
    index: number
): string => {
    const id = requirement.id?.trim();
    if (id) return id;
    return `requirement-${index}-${requirement.code?.trim() || requirement.name?.trim() || 'item'}`;
};

const resolveAbsoluteFileUrl = (fileUrl?: string): string | undefined => {
    const trimmed = fileUrl?.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return undefined;
};

type MemberDetailsModalProps = {
    member: MembershipSchema
    handleClose: () => void;
  }

const MemberDetails: React.FC<MemberDetailsModalProps> = ({member, handleClose}) => {

    const [messageOpen, setMessageOpen] = useState(false);
    const { currentMember, isLoadingCurrentMember } = useLoadMember(member?.uid ? member.uid : "")
    // var memberUsername = currentMember?.username ? currentMember.username : "";
    const memberUid = currentMember?.uid ? currentMember.uid : "";
    // console.log("requirements", requirements)
    
    const communityId = member?.community_id ? member.community_id : ""
    const { memberRequirementStatuses, isLoadingRequirementsMet, approveRequirementCredential, rejectRequirementCredential } = useGetMemberRequirementsStatus(memberUid, communityId)
    const { agreement: savedAgreement, agreementLoading } = useGetMemberAgreement(communityId)

    const memberAgreementFileUrl = resolveAbsoluteFileUrl(currentMember?.member_agreement?.file_url);

    const displayRequirementStatuses = useMemo(() => {
        const statuses = Array.isArray(memberRequirementStatuses) ? [...memberRequirementStatuses] : [];
        if (!savedAgreement?.last_updated) return statuses;
        const submitted = currentMember?.member_agreement?.submitted;
        const submittedAt = submitted ? moment(submitted) : null;
        const isAgreementMet = !!submittedAt?.isValid() && !submittedAt.isAfter(moment());
        const agreementRequirement: CurrentRequirementsSchema = {
            id: MEMBERSHIP_AGREEMENT_REQUIREMENT_ID,
            code: MEMBERSHIP_AGREEMENT_REQUIREMENT_CODE,
            name: 'Sign Membership Agreement',
            is_met: isAgreementMet,
            is_required: true,
            presigned_url: memberAgreementFileUrl || '',
            expiration: '',
            credential_number: '',
            municipality: '',
        };
        return [agreementRequirement, ...statuses];
    }, [memberRequirementStatuses, savedAgreement?.last_updated, currentMember?.member_agreement?.submitted, memberAgreementFileUrl]);

    const openRequirementPreview = (requirement: CurrentRequirementsSchema) => {
        if (isPaymentRequirement(requirement)) return;
        if (isMembershipAgreementRequirement(requirement)) {
            const fileUrl = memberAgreementFileUrl || resolveAbsoluteFileUrl(requirement.presigned_url);
            if (!fileUrl) {
                toast.error('Member agreement file link is unavailable or invalid.');
                return;
            }
            setDocumentPreviewOpen({
                credential: { ...requirement, presigned_url: fileUrl },
                isOpen: true,
            });
            return;
        }
        setDocumentPreviewOpen({ credential: requirement, isOpen: true });
    };

    const currentMemberJoined = moment(currentMember?.joined).tz('America/New_York').startOf('second').fromNow()

    // const isExpired = (dateString: string) => {
    //     // Parse the 'MM/YYYY' formatted date into a TypeScript Date object
    //     const dateParts = dateString.split('/');
    //     if (dateParts.length === 2) {
    //     const month = parseInt(dateParts[0], 10); // Parse the month part
    //     const year = parseInt(dateParts[1], 10);  // Parse the year part

    //         // Check if month and year are valid numbers
    //         if (!isNaN(month) && !isNaN(year)) {
    //             // Create a Date object using the parsed values
    //             const parsedDate = new Date(year, month - 1, 1); // Subtract 1 from the month since months are zero-based

    //             // Get the current date
    //             const currentDate = new Date();

    //             // Compare the parsed date with the current date
    //             if (parsedDate < currentDate) {
    //                 return true
    //             }   
    //         }
    //     }
    //     return false
    // }


    // const pay_requirements_length = (currentMember && currentMember?.plan_recurrence && !['hourly', 'daily'].includes(currentMember.plan_recurrence) && (requirements?.length || requirements?.length === 0)) ? requirements.length + 1 : requirements?.length


    const percentRequirementsMet = (): number | null => {
        if (isLoadingRequirementsMet || agreementLoading) return null;
        if (!displayRequirementStatuses.length) return 100;
        const metCount = displayRequirementStatuses.filter(req => req.is_met).length;
        return (metCount / displayRequirementStatuses.length) * 100;
    };
    
    const requirementsMet = percentRequirementsMet()

    // const capitalizeWords = (str: string) => {
    //     if (str){
    //       // Split the string into an array of words
    //       const words = str.split(' ');
    //       // Capitalize the first letter of each word
    //       const capitalizedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
    //       // Join the capitalized words back into a string
    //       const capitalizedString = capitalizedWords.join(' ');
    //       return capitalizedString;      
    //     }
    // }

    type DocumentPreviewOpen = {
        credential: CurrentRequirementsSchema
        isOpen: boolean
    }

    const [documentPreviewOpen, setDocumentPreviewOpen] = useState<DocumentPreviewOpen>({
        credential: displayRequirementStatuses.length > 0 ? displayRequirementStatuses[0] : {is_met: false} as CurrentRequirementsSchema,
        isOpen: false
    })
    
    
    const handleCloseDocumentPreview = () => {
        setDocumentPreviewOpen({credential: {is_met: false} as CurrentRequirementsSchema, isOpen: false})
    }


    const handleApprove = (credentialId: string, updatedCredentialDetails: CurrentRequirementsSchema) => {
        if (credentialId && updatedCredentialDetails.is_met) {
            // console.log("is_required and is_met and (credential_number or municipality)")
            approveRequirementCredential(credentialId, updatedCredentialDetails)
        } 
        setDocumentPreviewOpen({credential: {is_met: false} as CurrentRequirementsSchema, isOpen: false})

        // else {
        //     console.warn("not_required or not_met or credential_number or municipality or credentialId")
        // }
    }

    const handleReject = (credentialId: string) => {
        if (credentialId) {
            // console.log("credentialId", credentialId)
            rejectRequirementCredential(credentialId)
        } 
        setDocumentPreviewOpen({credential: {is_met: false} as CurrentRequirementsSchema, isOpen: false})
        // else {
        //     console.warn("no credentialId")
        // }
    }

    // console.log("documentPreviewOpen", documentPreviewOpen)

    const [imageError, setImageError] = useState(false);
    const avatar = currentMember?.avatar;
    const { updateMembership } = useUpdateMembership(memberUid);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const statusButtonRef = useRef<HTMLButtonElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    useEffect(() => {
        if (statusDropdownOpen && statusButtonRef.current) {
            const rect = statusButtonRef.current.getBoundingClientRect();
            setDropdownStyle({ width: rect.width, minWidth: rect.width, maxWidth: rect.width });
        }
    }, [statusDropdownOpen]);

    const nextBillingCycleLabel =
        currentMember?.joined && currentMember?.plan_recurrence
            ? getNextBillingCycleLabel(currentMember.joined, currentMember.plan_recurrence, currentMember.plan_rate)
            : null;

    const [localStatus, setLocalStatus] = useState<MembershipStatusType | undefined>(member?.status as MembershipStatusType);
    useEffect(() => {
        if (currentMember?.status) setLocalStatus(currentMember?.status as MembershipStatusType)
    }, [currentMember?.status])
    const statusColors = getMemberStatusColor(localStatus || '');

    return documentPreviewOpen.isOpen ? (
        <CredentialPreview 
            credential={documentPreviewOpen.credential} 
            handleClose={handleCloseDocumentPreview}
            onApprove={handleApprove}
            onReject={handleReject}
            onCancel={handleCloseDocumentPreview}
        />
    ) : (
        <div  className='flex flex-col justify-around'>
            <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
                <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
            </span>
            <div className='my-auto mx-2 font-raleway text-[20px] text-black text-left font-semibold p-1'>
                Member Profile
            </div>
            <div className='flex flex-row p-1 my-2'>

                {(isLoadingCurrentMember || isLoadingRequirementsMet) ? (
                    <Skeleton className="h-[56px] w-[56px] rounded-full mr-2 shrink-0" data-testid="member-details-profile-skeleton" />
                ) : avatar && !imageError ? (
                    <div
                        className="flex border-[#E9E9E9] border-[1px] rounded-full h-[56px] w-[56px] mr-2"
                    >
                        <img
                            src={avatar}
                            alt="User Avatar"
                            className="h-[56px] w-[56px] m-auto rounded-full cursor-pointer"
                            onError={() => setImageError(true)}
                        />
                    </div>
                ) : (
                    <UserAvatar
                        firstName={currentMember?.first_name}
                        lastName={currentMember?.last_name}
                        username={currentMember?.username}
                        email={currentMember?.email}
                        size="lg"
                        className="mr-2 font-open-sans"
                    />
                )}

                <div className='flex flex-col mx-2 flex-1 min-w-0'>
                    {(isLoadingCurrentMember || isLoadingRequirementsMet) ? (
                        <div className="flex flex-col gap-2" data-testid="member-details-name-skeleton">
                            <Skeleton className="h-4 w-36" />
                            <Skeleton className="h-3 w-28" />
                        </div>
                    ) : (
                        <>
                            <p className='text-[16px] font-raleway text-black font-medium'>
                                {currentMember?.first_name} &nbsp;
                                {currentMember?.last_name}
                            </p>
                            <p className='text-[14px] font-open-sans text-[#4D4D4D] font-normal'>
                                Joined {currentMemberJoined}
                            </p>
                        </>
                    )}
                </div>


                <div className='flex flex-col ml-auto w-[220px]'>
                    {(isLoadingCurrentMember || isLoadingRequirementsMet || agreementLoading) ? (
                        <div className='flex flex-col gap-2' data-testid="member-details-completion-skeleton">
                            <Skeleton className="h-3 w-40" />
                            <Skeleton className="h-2 w-full rounded-full" />
                        </div>
                    ) : requirementsMet !== null && (
                        <div className='flex flex-col'>
                            <div className='flex items-center justify-between'>
                                <p className='text-[13px] font-raleway text-[#4D4D4D] font-medium whitespace-nowrap'>{requirementsMet.toFixed(0)}% Profile Completion</p>
                            </div>
                            <div className='w-full h-[8px] bg-[#E9E9E9] rounded-full overflow-hidden'>
                                <div
                                    className={`${requirementsMet < 50 ? 'bg-[#D42620]' : 'bg-[#0F973D]'} h-full rounded-full`}
                                    style={{ width: `${Math.min(100, Math.max(0, requirementsMet))}%` }}
                                />
                            </div>
                        </div>
                    )}

                    <div className='flex items-center mt-2 font-open-sans'>
                        <div className='relative mx-auto w-full'>
                            {(isLoadingCurrentMember || isLoadingRequirementsMet) ? (
                                <Skeleton className="h-10 w-full rounded-lg" data-testid="member-details-status-skeleton" />
                            ) : (
                            <button
                                onClick={() => setStatusDropdownOpen(prev => !prev)}
                                className="flex items-center space-x-2 px-3 py-2 border-[1px] border-[#E9E9E9] rounded-lg text-sm font-semibold shadow-sm w-full whitespace-nowrap"
                                style={{ color: statusColors.primaryTextColor, backgroundColor: '#FFFFFF' }}
                                ref={statusButtonRef}
                            >
                                <span className="w-3 h-3 rounded-full flex items-center justify-center" style={{ backgroundColor: statusColors.bgColor }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColors.primaryTextColor }} />
                                </span>
                                <span className='flex-1 overflow-hidden text-ellipsis whitespace-nowrap'>
                                    {`Membership ${localStatus ? (localStatus as string).replace(/\b\w/g, (s: string) => s.toUpperCase()) : 'Pending'}`}
                                </span>
                                {statusDropdownOpen ? <ExpandLess className="w-4 h-4" /> : <ExpandMore className="w-4 h-4" />}
                            </button>
                            )}
                            {statusDropdownOpen && !isLoadingCurrentMember && (() => {
                                const membershipStatuses = getMemberStatusUpdateOptions(localStatus as MembershipStatusType)
                                  .filter((opt: MemberRecordDropdownOptionsType) => typeof opt.value === 'string' && opt.value !== 'details' && opt.value !== 'message');
                                return (
                                  <ul 
                                    className="absolute left-0 mt-2 border border-gray-200 rounded-lg bg-white shadow-lg z-20"
                                    style={dropdownStyle}
                                  >
                                    {membershipStatuses.map((option: MemberRecordDropdownOptionsType, i: number) => {
                                      const IconComponent = option.icon as any;
                                      return (
                                        <span
                                          key={`${option.value}-${i}`}
                                          className={`flex flex-row items-center font-open-sans text-left text-[14px] font-medium cursor-pointer px-3 py-2 hover:bg-gray-100 ${option.textColor} ${option.bgColor} ${i !== membershipStatuses.length - 1 ? 'border-b border-gray-100' : ''}`}
                                          onClick={async () => {
                                            try{
                                              const res = await updateMembership({ username: currentMember?.username, status: option.value as MembershipStatusType } as MembershipSchema)
                                              if (res) { 
                                                setLocalStatus(option.value as MembershipStatusType);
                                                toast.success('Member status updated'); 
                                              } else {
                                                toast.error('Update failed');
                                              }
                                            } catch {
                                              toast.error('Update failed');
                                            } finally {
                                              setStatusDropdownOpen(false);
                                            }
                                          }}
                                        >
                                          {IconComponent && (
                                            <IconComponent style={{fontSize: 18}} className={`${option.textColor || 'text-[#A1A1A1]/70'}`} />
                                          )}
                                          <p className="text-[14px] font-medium mx-2">{option.label}</p>
                                        </span>
                                      );
                                    })}
                                  </ul>
                                );
                              })()}
                        </div>
                    </div>
                </div>
            </div>
        
            <div className='flex flex-col p-1'>
                <p className='text-[14px] font-raleway text-[#4D4D4D] font-medium my-1'>Contact Details</p>
                {(isLoadingCurrentMember || isLoadingRequirementsMet) ? (
                    <div className='flex flex-row w-full gap-4' data-testid="member-details-contact-skeleton">
                        <Skeleton className="h-16 w-1/2 rounded-md" />
                        <Skeleton className="h-16 w-1/2 rounded-md" />
                    </div>
                ) : (
                    <div className='flex flex-row w-full'>
                        <div className='flex flex-col border-[1px] border-[#E9E9E9] rounded-md p-2 w-1/2'>
                            <p className='text-[#808080] text-[16px] font-raleway'>Email</p>
                            <p className='font-open-sans text-black text-[16px]'>{currentMember?.email}</p>
                        </div>
                        <div className='flex flex-col ml-4 border-[1px] border-[#E9E9E9] rounded-md p-2 w-1/2'>
                            <p className='text-[#808080] text-[16px] font-raleway'>Location</p>
                            <p className='font-open-sans text-black text-[16px]'>{currentMember?.state}</p>
                        </div>
                    </div>
                )}
            </div>

            <div className='flex flex-col p-1'>
                <p className='text-[14px] font-raleway text-[#4D4D4D] font-medium my-1'>Membership Tier</p>
                <div className='flex flex-row w-full'>
                    <div className='flex flex-row w-full border-[1px] border-[#E9E9E9] rounded-md p-3 bg-white'>
                        <div className='flex flex-col'>
                        {(isLoadingCurrentMember || isLoadingRequirementsMet) ? (
                            <div className="flex flex-col gap-2" data-testid="member-details-tier-skeleton">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-48" />
                            </div>
                        ) : (
                            (currentMember && currentMember?.plan_rate) ? (
                                <>
                                    <p className='text-black text-[16px] font-raleway font-medium'>
                                        {
                                            currentMember.plan_name
                                        }
                                    </p>
                                    <p className='text-[#4D4D4D] text-[14px] font-open-sans'>
                                        {
                                            (currentMember?.plan_rate.toString().includes(".") ) ? (
                                                `$${styleThousandCommas(currentMember.plan_rate.toString())} (${currentMember.plan_recurrence})`
                                            ) : (
                                                `$${styleThousandCommas(currentMember?.plan_rate.toString())}.00 (${currentMember.plan_recurrence})`
                                            )
                                        }
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className='text-black/70 text-[15px] font-medium font-raleway'>
                                        Free
                                    </p>
                                    <p className='text-[#4D4D4D]/70 italic text-[14px] font-normal font-open-sans'>
                                        No Tier Selected Yet
                                    </p>
                                </>
                            )
                        )}
                        </div>
                        {nextBillingCycleLabel && (
                            <p
                                className='text-[#4D4D4D] text-[12px] font-raleway font-medium mt-1 ml-auto text-right'
                                data-testid="member-details-next-billing"
                            >
                                {nextBillingCycleLabel}
                            </p>
                        )}
                    </div>
                    
                </div>
            </div>


            <div className='flex flex-col p-1'>
                <p className='text-[14px] font-raleway text-[#4D4D4D] font-medium my-1'>Credentials</p>
                <div className='flex flex-row w-full'>
                    <div className='flex flex-col  w-full'>
                        {(isLoadingCurrentMember || isLoadingRequirementsMet || agreementLoading) ? (
                            <div className="flex flex-col gap-2" data-testid="member-details-credentials-skeleton">
                                {[0, 1, 2].map((i) => (
                                    <Skeleton key={i} className="h-12 w-full rounded-md" />
                                ))}
                            </div>
                        ) : (
                            displayRequirementStatuses.map((requirement: CurrentRequirementsSchema, i: number)=> {
                                const isMembershipAgreement = isMembershipAgreementRequirement(requirement);
                                const isOrientationTraining = isOrientationTrainingRequirement(requirement);
                                const isPayment = isPaymentRequirement(requirement);
                                const isRowClickable = isRequirementRowClickable(
                                    requirement,
                                    isMembershipAgreement,
                                    memberAgreementFileUrl
                                );
                                const statusDisplay = getRequirementStatusLabel(
                                    requirement,
                                    isMembershipAgreement,
                                    isOrientationTraining,
                                    isPayment
                                );
                                const isStandardCredential =
                                    !isMembershipAgreement && !isOrientationTraining && !isPayment;
                                const expirationInfo =
                                    requirement.is_met && isStandardCredential
                                        ? getCredentialExpirationInfo(requirement.expiration)
                                        : null;
                                const effectiveStatusDisplay =
                                    expirationInfo?.status === 'expired'
                                        ? { label: expirationInfo.statusLabel!, tone: 'error' as const }
                                        : expirationInfo?.status === 'expiring_soon'
                                            ? { label: expirationInfo.statusLabel!, tone: 'warning' as const }
                                            : statusDisplay;
                                const rowSurfaceClass =
                                    expirationInfo?.status === 'expired'
                                        ? 'border-[#F5C6CB] bg-red-50/40'
                                        : expirationInfo?.status === 'expiring_soon'
                                            ? 'border-amber-200 bg-amber-50/40'
                                            : 'border-[#E9E9E9]/70';
                                const rowHoverClass = isRowClickable
                                    ? isMembershipAgreement || isOrientationTraining
                                        ? 'hover:bg-[#EFF8FA]/70 cursor-pointer'
                                        : expirationInfo?.status === 'expired'
                                            ? 'hover:bg-red-50/70 cursor-pointer'
                                            : expirationInfo?.status === 'expiring_soon'
                                                ? 'hover:bg-amber-50/70 cursor-pointer'
                                                : 'hover:bg-[#f5f5f5]/50 cursor-pointer'
                                    : '';
                                const iconBoxClass =
                                    expirationInfo?.status === 'expired'
                                        ? 'bg-red-100'
                                        : expirationInfo?.status === 'expiring_soon'
                                            ? 'bg-amber-100'
                                            : 'bg-[#EFF8FA]';
                                const requirementSubtitle = isMembershipAgreement
                                    ? 'Membership agreement'
                                    : isOrientationTraining
                                        ? 'Orientation or training'
                                        : isPayment
                                            ? 'Membership payment method'
                                            : expirationInfo?.detailLabel ?? null;
                                const statusTextClass =
                                    effectiveStatusDisplay.tone === 'success'
                                        ? 'text-[#0A7A32]'
                                        : effectiveStatusDisplay.tone === 'warning'
                                            ? 'text-[#d45a02]'
                                            : 'text-[#B01E1A]';
                                const StatusIcon =
                                    effectiveStatusDisplay.tone === 'success'
                                        ? CheckCircle
                                        : effectiveStatusDisplay.tone === 'warning'
                                            ? ReportProblem
                                            : Error;
                                const statusIconColor =
                                    effectiveStatusDisplay.tone === 'success'
                                        ? 'success'
                                        : effectiveStatusDisplay.tone === 'warning'
                                            ? 'warning'
                                            : 'error';
                                return (
                                <div 
                                    key={getRequirementRowKey(requirement, i)} 
                                    className={`flex flex-row border-[1px] rounded-md p-2 my-1 w-full ${rowSurfaceClass} ${rowHoverClass}`}
                                    onClick={isRowClickable ? () => openRequirementPreview(requirement) : undefined}
                                >
                                    <div className={`flex h-[32px] w-[32px] rounded-md mr-2 my-auto ${iconBoxClass}`}>
                                        {isMembershipAgreement ? (
                                            <DescriptionOutlined className='text-primary-300 m-auto' sx={{ fontSize: 20 }} />
                                        ) : isOrientationTraining ? (
                                            <SchoolOutlined className='text-primary-300 m-auto' sx={{ fontSize: 20 }} />
                                        ) : isPayment ? (
                                            <CreditCard className='text-primary-300 m-auto' sx={{ fontSize: 20 }} />
                                        ) : (
                                            <CredentialIcon className='text-primary-300 m-auto' />
                                        )}
                                    </div>
                                    
                                    <div className='flex flex-col my-auto justify-center min-w-0'>
                                        <div className='flex flex-row items-center'>
                                            <p className={`my-auto font-raleway text-[16px] text-black font-medium truncate ${isRowClickable ? 'hover:text-primary-400 cursor-pointer' : ''}`}>{requirement.name}</p> 
                                            {isRowClickable && (
                                                <Launch sx={{ fontSize: 15 }} className='my-auto text-[#ccc] ml-2 shrink-0 cursor-pointer hover:text-[#aaa]' />
                                            )}
                                        </div>
                                        {requirementSubtitle && (
                                            <p
                                                className={`text-[12px] font-open-sans mt-0.5 ${
                                                    expirationInfo?.status === 'expired'
                                                        ? 'text-[#B01E1A]'
                                                        : expirationInfo?.status === 'expiring_soon'
                                                            ? 'text-[#d45a02]'
                                                            : 'text-[#808080]'
                                                }`}
                                                data-testid={
                                                    expirationInfo?.status && expirationInfo.status !== 'none'
                                                        ? 'credential-expiration-detail'
                                                        : undefined
                                                }
                                            >
                                                {requirementSubtitle}
                                            </p>
                                        )}
                                    </div>

                                
                                    <div className='flex flex-row ml-auto my-auto shrink-0'>
                                        <div className='flex flex-row my-auto'>
                                            <p className={`m-auto text-right text-[12px] font-open-sans font-medium ${statusTextClass}`}>
                                                {effectiveStatusDisplay.label}
                                            </p>&nbsp;
                                            <StatusIcon sx={{ fontSize: 18 }} color={statusIconColor as 'success' | 'warning' | 'error'} className='my-auto' />
                                        </div>
                                    </div>

                                </div>
                            );
                            })
                        )}

                    </div>
                </div>
            </div>


            <div className='flex flex-col p-1'>
                <button 
                    onClick={() => setMessageOpen(true)} 
                    className="px-5 py-2.5 border border-[#E9E9E9] font-raleway rounded-md text-gray-700 font-semibold hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 text-[16px]">
                    Send a Message
                </button>
            </div>

            {
                (currentMember?.username && messageOpen) && (
                    <NewMessageModal 
                        username={currentMember?.username}
                        isOpen={messageOpen} 
                        closeAppModal={() => setMessageOpen(false)} 
                    />
                )
            }
        </div>
    )
}

export default MemberDetails